<template>
  <div class="IndicatorPopup PlatformMapPopup MyMapResourcePopupContent">
    <component
      :is="resourceFileHref !== '' ? 'a' : 'div'"
      class="PlatformMapPopup__linkedBlock"
      v-bind="resourceLinkAnchorAttrs"
    >
      <div class="IndicatorPopup__title">{{ displayTitle }}</div>

      <div v-if="coverImageUrl !== ''" class="PlatformMapPopup__figure">
        <img
          class="PlatformMapPopup__coverImg"
          :src="coverImageUrl"
          :alt="coverImageAlt"
          loading="lazy"
        />
      </div>

      <div v-if="locationLabel !== ''" class="PlatformMapPopup__location">
        <Icon
          icon="mdi:map-marker-outline"
          class="PlatformMapPopup__locationIcon"
          color="currentColor"
          :size="18"
        />
        <span>{{ locationLabel }}</span>
      </div>

      <div class="IndicatorPopup__content">
        <div
          v-if="resourceDescriptionRaw !== ''"
          class="IndicatorPopup__value IndicatorPopup__value--multiline IndicatorPopup__value--html"
          v-html="sanitizedResourceDescriptionHtml"
        />
        <span v-else class="IndicatorPopup__value IndicatorPopup__value--no-data">N/A</span>
      </div>

      <div v-if="hasMetaBlock" class="PlatformMapPopup__meta">
        <p v-if="updatedLine !== ''" class="PlatformMapPopup__metaLine">{{ updatedLine }}</p>
        <p v-if="authorLine !== ''" class="PlatformMapPopup__metaLine">{{ authorLine }}</p>
      </div>

      <span class="PlatformMapPopup__tag">{{ typeLabel }}</span>
    </component>
  </div>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import type { Resource } from '@/models/interfaces/Resource'
import type { User } from '@/models/interfaces/auth/User'
import { resolveMediaObjectDisplayUrl, resolveMediaUrl } from '@/config/env'
import GeocodingService from '@/services/map/GeocodingService'
import { ResourceService } from '@/services/resources/ResourceService'
import { sanitizeRichTextHtml } from '@/services/utils/sanitizeRichTextHtml'
import { localizeDate } from '@/services/utils/UtilsService'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  resource: Resource
}>()

const { t, te } = useI18n()

const displayTitle = computed(() => {
  const nameRaw = props.resource.name
  return nameRaw != null && String(nameRaw).trim() !== '' ? String(nameRaw).trim() : '—'
})

const coverImageUrl = computed(() => {
  const fromPreview = resolveMediaObjectDisplayUrl(props.resource.previewImage)
  if (fromPreview !== '') return fromPreview
  return resolveMediaObjectDisplayUrl(props.resource.logo)
})

const coverImageAlt = computed(() => displayTitle.value)

const locationLabel = computed(() => {
  const geo = props.resource.geoData
  if (geo == null) return ''
  return GeocodingService.getLocationName(geo).trim()
})

const typeLabel = computed(() => {
  const key = `resources.resourceType.${props.resource.type}`
  return te(key) ? String(t(key)) : String(props.resource.type)
})

function authorDisplayName(user: User | null | undefined): string {
  if (user == null) return ''
  const parts = [user.userPrenom, user.userNom].filter((p) => p != null && String(p).trim() !== '')
  const joined = parts.join(' ').trim()
  if (joined !== '') return joined
  return user.userEmail ?? ''
}

const authorForMeta = computed(() => {
  const fromUser = authorDisplayName(props.resource.createdBy)
  if (fromUser !== '') return fromUser
  const fromField = props.resource.author?.trim()
  return fromField != null && fromField !== '' ? fromField : ''
})

const updatedLine = computed(() => {
  const dateRaw = props.resource.updatedAt
  if (dateRaw == null) return ''
  const dateStr = localizeDate(dateRaw, { year: 'numeric', month: 'long', day: 'numeric' })
  if (dateStr === '') return ''
  return t('myMap.resourcePopup.updatedOn', { date: dateStr })
})

const authorLine = computed(() => {
  const author = authorForMeta.value
  if (author === '') return ''
  return t('myMap.resourcePopup.byAuthor', { author })
})

const hasMetaBlock = computed(() => updatedLine.value !== '' || authorLine.value !== '')

const resourceDescriptionRaw = computed(() =>
  props.resource.description != null ? String(props.resource.description).trim() : '',
)

const sanitizedResourceDescriptionHtml = computed(() =>
  sanitizeRichTextHtml(resourceDescriptionRaw.value),
)

/** Absolute URL to the resource file (contentUrl or legacy link), for the primary popup anchor. */
const resourceFileHref = computed(() => {
  const raw = ResourceService.getLink(props.resource)
  const trimmed = raw != null ? String(raw).trim() : ''
  if (trimmed === '') return ''
  return resolveMediaUrl(trimmed)
})

const resourceLinkAnchorAttrs = computed((): Record<string, string> => {
  if (resourceFileHref.value === '') return {}
  return {
    href: resourceFileHref.value,
    target: '_blank',
    rel: 'noopener noreferrer',
  }
})
</script>

<style lang="scss" scoped>
@use './indicator-popup.shared.scss' as *;
@use './platform-map-popup.shared.scss' as *;

.IndicatorPopup__value--html {
  display: block;

  :deep(p) {
    margin: 0 0 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.5rem;
    padding-left: 1.25rem;
  }

  :deep(li) {
    margin-bottom: 0.25rem;
  }

  :deep(code) {
    font-size: 0.85em;
    padding: 0.1em 0.3em;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.06);
  }

  :deep(pre) {
    margin: 0 0 0.5rem;
    padding: 0.5rem 0.65rem;
    overflow-x: auto;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
    font-size: 0.8125rem;
  }

  :deep(pre code) {
    padding: 0;
    background: transparent;
  }

  :deep(a) {
    color: rgb(var(--v-theme-main-blue));
    text-decoration: underline;
  }
}
</style>
